import React, { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { ActivitySection, useActivityReplyHandler } from 'src/components';
import type { OnSubmitCommentReturn } from 'src/components';
import { useActivityNavigation } from 'src/components/activity/use-activity-navigation';
import { useStartAiThreadFromActivity } from 'src/components/activity/use-start-ai-thread';
import { useCurrentUserId } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { workflowTypename } from 'src/helpers';
import { useCreateComment, useWorkflowActivity } from 'src/hooks';

import { useThreadDeepLinkHandlers } from './use-thread-deep-link-handlers';

interface WorkflowActivitySectionProps {
  workflowId: UUID;
}

export function WorkflowActivitySection({ workflowId }: WorkflowActivitySectionProps): JSX.Element {
  const currentUserId = useCurrentUserId();
  const navigation = useActivityNavigation(workflowId);
  const {
    activity,
    fetchMoreActivity,
    fetchPreviousActivity,
    hasMore,
    hasPrevious,
    loadedAnchor,
    loading,
    loadingMore,
    loadingPrevious,
  } = useWorkflowActivity(workflowId, {
    anchor: navigation.request?.activityItemId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { createComment } = useCreateComment();
  const { onViewThread } = useThreadDeepLinkHandlers({ workflowId });

  const handleCreateComment = useCallback(
    async (markdown: string): Promise<OnSubmitCommentReturn> => {
      if (!currentUserId) return null;
      const { data } = await createComment(workflowId, workflowTypename, {
        variables: {
          input: {
            commentProps: {
              authorId: currentUserId,
              commentableId: workflowId,
              commentableType: Apollo.CommentableType.Workflow,
              nodeRefId: null,
              parentCommentId: null,
              text: markdown,
            },
          },
        },
      });
      return data?.createComment.comment ?? null;
    },
    [createComment, currentUserId, workflowId]
  );

  const handleSubmitReply = useActivityReplyHandler({
    commentableId: workflowId,
    commentableType: Apollo.CommentableType.Workflow,
  });

  const handleSubmitAI = useStartAiThreadFromActivity({ workflowId });

  return (
    <ActivitySection
      fetchMoreActivity={fetchMoreActivity}
      fetchPreviousActivity={fetchPreviousActivity}
      hasMore={hasMore}
      hasPrevious={hasPrevious}
      items={activity}
      loadedAnchor={loadedAnchor}
      loading={loading}
      loadingMore={loadingMore}
      loadingPrevious={loadingPrevious}
      navigation={navigation}
      placeholder="@ mention a user or AI in a comment…"
      showHeader={false}
      workflowId={workflowId}
      onSubmitAI={handleSubmitAI}
      onSubmitComment={handleCreateComment}
      onSubmitReply={handleSubmitReply}
      onViewThread={onViewThread}
    />
  );
}
